import React, { useState, useEffect, useRef } from 'react';
import { User, Search, ChevronDown, X, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAdminId } from '../../utils/getAdminId';
import { WorkerInfo } from '../../types';

interface WorkerSelectorProps {
  selectedWorkerId: string | null;
  onSelect: (worker: WorkerInfo | null) => void;
  disabled?: boolean;
  placeholder?: string;
  autoSelectCurrent?: boolean;
}

export const WorkerSelector: React.FC<WorkerSelectorProps> = ({
  selectedWorkerId,
  onSelect,
  disabled = false,
  placeholder = 'Select worker...',
  autoSelectCurrent = true
}) => {
  const { user } = useAuth();
  const [workers, setWorkers] = useState<WorkerInfo[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const adminId = getAdminId(user);

  useEffect(() => {
    if (adminId) {
      fetchWorkers();
    }
  }, [adminId]);

  useEffect(() => {
    if (autoSelectCurrent && user && workers.length > 0 && !selectedWorkerId) {
      const currentWorker = workers.find(w => w.id === user.id);
      if (currentWorker) {
        onSelect(currentWorker);
      }
    }
  }, [workers, user, autoSelectCurrent, selectedWorkerId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchWorkers = async () => {
    if (!adminId) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, email, role, avatar_url')
        .or(`id.eq.${adminId},admin_id.eq.${adminId}`)
        .in('role', ['admin', 'worker'])
        .eq('is_active', true)
        .order('full_name');

      if (error) throw error;
      setWorkers(data || []);
    } catch (err) {
      console.error('Error fetching workers:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);

  const filteredWorkers = workers.filter(w =>
    w.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (worker: WorkerInfo) => {
    onSelect(worker);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setIsOpen(!isOpen); } }}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-white dark:bg-gray-900 border rounded-lg text-left transition-all ${
          disabled
            ? 'border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60'
            : isOpen
            ? 'border-teal-500 ring-2 ring-teal-500/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedWorker ? (
            <>
              <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
                {selectedWorker.avatar_url ? (
                  <img
                    src={selectedWorker.avatar_url}
                    alt={selectedWorker.full_name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {selectedWorker.full_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {selectedWorker.email}
                </p>
              </div>
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${
                selectedWorker.role === 'admin'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              }`}>
                {selectedWorker.role}
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedWorker && !disabled && (
            <button
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search workers..."
                className="w-full pl-8 pr-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                <div className="animate-spin h-5 w-5 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-2" />
                Loading workers...
              </div>
            ) : filteredWorkers.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                No workers found
              </div>
            ) : (
              filteredWorkers.map(worker => (
                <button
                  key={worker.id}
                  type="button"
                  onClick={() => handleSelect(worker)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    worker.id === selectedWorkerId ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    {worker.avatar_url ? (
                      <img
                        src={worker.avatar_url}
                        alt={worker.full_name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {worker.full_name}
                      {worker.id === user?.id && (
                        <span className="ml-1.5 text-xs text-teal-600 dark:text-teal-400">(You)</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {worker.email}
                    </p>
                  </div>
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${
                    worker.role === 'admin'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    {worker.role}
                  </span>
                  {worker.id === selectedWorkerId && (
                    <Check className="h-4 w-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
